import { Link } from "react-router-dom";
import { ArrowRight, Cog, MapPin, Wrench } from "lucide-react";
import { MaskedLines, Reveal, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const STORY_IMG =
  "https://images.pexels.com/photos/33968618/pexels-photo-33968618.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CITY_IMG =
  "https://images.unsplash.com/photo-1763041821508-e441d40b1e41?crop=entropy&cs=srgb&fm=jpg&q=85";

const VALUES = [
  {
    icon: MapPin,
    title: "NZ Based",
    body: "Auckland owned and operated. Every order lands with a real person in Auckland — not a warehouse overseas.",
  },
  {
    icon: Cog,
    title: "Quality Components",
    body: "We hand-pick motors, batteries, controllers and displays we'd run on our own bikes, sourced from a trusted supplier.",
  },
  {
    icon: Wrench,
    title: "Delivery, Your Way",
    body: "Shipping or Auckland pickup/drop-off is a flat $170 NZ-wide. Full installation by us is $200 — drop your bike off, or have the kit shipped to us. Accessories ride free with a kit.",
  },
];

export default function About() {
  return (
    <div>
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <p className="eyebrow mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          About Auckland E-Kits
        </p>
        <h1
          className="font-display font-black tracking-tight text-ink leading-[0.95]"
          style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}
          data-testid="about-heading"
        >
          <MaskedLines
            lines={[
              <span key="a">BUILT IN AUCKLAND.</span>,
              <span key="b">
                RIDDEN <span className="text-volt">EVERYWHERE.</span>
              </span>,
            ]}
          />
        </h1>
      </section>

      <section className="border-y border-black/10 bg-surface">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:items-center">
          <Reveal className="relative overflow-hidden rounded-2xl border border-black/10">
            <img
              src={STORY_IMG}
              alt="Cyclist on an Auckland street"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </Reveal>
          <div>
            <SectionHeading eyebrow="Why we exist" title="Keep your bike. Swap the powertrain." />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-zinc-600">
                <p>
                  A good bike already fits you, rides right and you love it.
                  Swapping the whole bike for an e-bike throws away everything
                  that made it yours. So we don't sell bikes — we sell the
                  conversion: motor, battery, controller, display, done.
                </p>
                <p>
                  Auckland E-Kits is a small operation. When you order, we
                  confirm stock with our supplier, invoice you directly, and
                  get it to you: shipped NZ-wide or Auckland pickup/drop-off on
                  a flat $170 — or fully installed by us for $200.
                </p>
                <p>
                  The 2000W Combo runs 52V, the 3000W Combo runs 72V — real
                  numbers from real spec sheets. Where we don't know yet, we
                  say so rather than guess.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <SectionHeading eyebrow="What we stand on" title="Three promises." />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="h-full">
              <div className="h-full rounded-2xl border border-black/10 bg-white p-8 transition-colors duration-300 hover:border-volt/40" data-testid={`about-value-${i}`}>
                <v.icon className="h-7 w-7 text-volt" strokeWidth={2} />
                <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight text-ink">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-600">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Power Your Ride", "Auckland E-Kits", "Keep your bike", "Swap the powertrain"]} duration={38} />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-black/10">
            <img src={CITY_IMG} alt="E-bike in city traffic" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-white/75" />
            <div className="relative flex flex-col items-start gap-6 p-10 sm:p-14">
              <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                Ready to make yours electric?
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link to="/shop" data-testid="about-cta-shop" className="rounded-[3px] bg-volt px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim">
                  Shop Kits
                </Link>
                <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 rounded-[3px] border border-black/25 bg-white/70 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-ink transition-colors duration-200 hover:border-volt hover:text-volt">
                  Ask a question <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
